import {useState, useEffect} from 'react';

/**
 * Given a value, creates a debounced value which will update when that value has not updated for
 * `timeout` milliseconds.
 *
 * This is useful for searches and behaviors that are not immediately triggered based on some value.
 *
 * @param {T} value - the value to track
 * @param {[number=500]} timeout - the amount of time to wait before setting the value
 * @returns {T} A reflection of the value
 * @example
 * ```tsx
 * function Search() {
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebouncedValue(search);
 *
 *   const {data, loading} = useQuery(dataQuery, {
 *     variables: {
 *       search: debouncedSearch,
 *     }
 *   });
 *
 *   const onSearch: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
 *     setSearch(value);
 *   };
 *
 *   return (
 *     <Stack>
 *       <Input type="search" value={search} onChange={onSearch} />
 *       <List loading={loading} data={data} />
 *     </Stack>
 *   )
 * }
 * ```
 */
export default function useDebouncedValue<T = unknown>(value: T, timeout: number = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedValue(value);
    }, timeout);
    return () => {
      window.clearTimeout(timer);
    };
  }, [timeout, value]);

  return debouncedValue;
}
